import React from 'react';
import dayjs from 'dayjs';
import env from 'api/env';
import { Button } from 'antd';
import cn from 'classnames';
// Types
import { SearchCampaignStores_searchCampaignStores_entities } from 'api/campaignV2/types/SearchCampaignStores';
import { GetBookMeStores_adminStores_entities } from 'api/store/types/GetBookMeStores';
import { GetStore_store } from 'api/store/types/GetStore';
import { GetStoresByNameAndEmail_emailStores_entities } from 'api/store/types/GetStoresByNameAndEmail';
import { SearchSocialShareCampaignStores_searchSocialShareCampaignStores_entities } from 'api/campaignV2/types/SearchSocialShareCampaignStores';
import { SearchSocialBoostCampaignStores_searchSocialBoostCampaignStores_entities } from 'api/campaignV2/types/SearchSocialBoostCampaignStores';
import { SearchCustomStores_searchCustomStores_entities } from 'api/campaignV2/types/SearchCustomStores';
// Helpers
import { formatAudienceSize } from 'helpers/campaignEditModal';
import { getEnvLink } from 'helpers/getEnvLink';
// Styles
import styles from './TalentItem.module.scss';

type TalentItemProps = {
  talent:
    | SearchCampaignStores_searchCampaignStores_entities
    | GetBookMeStores_adminStores_entities
    | GetStore_store
    | GetStoresByNameAndEmail_emailStores_entities
    | SearchSocialShareCampaignStores_searchSocialShareCampaignStores_entities
    | SearchSocialBoostCampaignStores_searchSocialBoostCampaignStores_entities
    | SearchCustomStores_searchCustomStores_entities;
  isVertical?: boolean;
  // this is only used for Book me campaigns to select a talent for the campaign
  onSelectTalent?: (talentId: string) => void;
  selectedTalentId?: string | null;
};

const TalentItem = ({
  talent,
  isVertical = false,
  onSelectTalent,
  selectedTalentId,
}: TalentItemProps): JSX.Element => {
  const talentName =
    talent.storeDetails?.storeName ?? `${talent.firstName} ${talent.lastName}`;

  const talentUrl = `${getEnvLink(env.REACT_APP_ENV)}/${talent?.slug}`;

  const handleViewProfile = () => {
    window.open(`${talentUrl}`, '_blank');
  };

  const isSelected = selectedTalentId === talent.id;

  return (
    <div
      className={cn(styles.root, {
        [styles.verticalRoot]: isVertical,
        [styles.selected]: isSelected,
      })}
    >
      <div
        className={cn(styles.imageWrapper, {
          [styles.verticalImageWrapper]: isVertical,
        })}
      >
        <img
          title={talentName}
          alt={talentName}
          src={talent.storeDetails?.avatarURL ?? ''}
        />
      </div>
      <div
        className={cn({
          [styles.verticalDataWrapper]: isVertical,
        })}
      >
        <div className={styles.detailsWrapper}>
          <div className={styles.sportsWrapper}>
            {talent.sports?.map((sport) => (
              <span key={sport.id} className={styles.sport}>
                {sport.name}
              </span>
            ))}
          </div>

          <div className={styles.talentName}>{talentName}</div>

          <div className={styles.detailsSection}>
            <div className={styles.row}>
              <span>Location:</span>
              <span>{talent.storeDetails?.country}</span>
            </div>

            <div className={styles.row}>
              <span>Age:</span>
              <span>
                {talent.storeDetails?.birthDate
                  ? dayjs().diff(dayjs(talent.storeDetails?.birthDate), 'year')
                  : 'N/a'}
              </span>
            </div>

            <div className={styles.row}>
              <span>Gender:</span>
              <span>{talent.storeDetails?.gender}</span>
            </div>

            <div className={styles.row}>
              <span>Followers:</span>
              <span>
                {formatAudienceSize(
                  talent.storeDetails?.socialFollowing?.toString() ?? '',
                  3
                )}
              </span>
            </div>
          </div>
        </div>
        {isVertical && (
          <>
            <div className={styles.viewProfileButtonWrapper}>
              <Button
                className={styles.viewProfileButton}
                onClick={handleViewProfile}
              >
                VIEW PROFILE
              </Button>
            </div>
          </>
        )}
      </div>

      {!isVertical && (
        <div className={styles.viewProfileButtonWrapper}>
          <Button onClick={handleViewProfile}>VIEW PROFILE</Button>
          {onSelectTalent && (
            <Button
              type="primary"
              onClick={() => onSelectTalent(talent.id)}
              disabled={isSelected}
            >
              {isSelected ? 'Selected' : 'Select'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default TalentItem;
